import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useRealtimeEvent, type UserEvent } from '@/features/realtime/RealtimeProvider';
import { listQueryOptions } from '@/lib/query-client';
import { makeQueryKeys } from '@/lib/query-keys';
import { toast } from '@/lib/toast';

import { telephonyService } from '../services/telephonyService';
import type {
  CallListParams,
  ClaimCallInput,
  DialCallInput,
  DidMappingInput,
  DismissReason,
  TelephonyAgentInput,
  UpdateDidMappingInput,
  UpdateTelephonyAgentInput,
} from '../types/schemas';

// `['telephony']` is also the root the realtime provider invalidates on a
// re-handshake and the pool listener invalidates on every pool-changed event.
export const telephonyKeys = makeQueryKeys('telephony');
export const didMappingKeys = makeQueryKeys('did-mappings');
export const telephonyAgentKeys = makeQueryKeys('telephony-agents');

export function useTelephonyCalls(params: CallListParams = {}) {
  return useQuery({
    queryKey: telephonyKeys.list(params),
    queryFn: () => telephonyService.listCalls(params),
    ...listQueryOptions,
  });
}

// Every pool mutation refetches on settle: a lost race (409) still moved the row.
function usePoolMutation<TVars, TResult>(mutationFn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all() });
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
    },
  });
}

export const useClaimCall = () =>
  usePoolMutation((vars: { id: string; input: ClaimCallInput }) =>
    telephonyService.claimCall(vars),
  );

export const useDismissCall = () =>
  usePoolMutation((vars: { id: string; reason: DismissReason }) =>
    telephonyService.dismissCall(vars),
  );

export const useReopenCall = () => usePoolMutation((id: string) => telephonyService.reopenCall(id));

export const useReassignCall = () =>
  usePoolMutation((vars: { id: string; practitionerId: string }) =>
    telephonyService.reassignCall(vars),
  );

/** Fetched only while the dial dialog is open: agent state is live data. */
export function useDialOptions({ enabled }: { enabled: boolean }) {
  return useQuery({
    queryKey: telephonyKeys.detail('dial-options'),
    queryFn: () => telephonyService.getDialOptions(),
    enabled,
    staleTime: 15_000,
  });
}

export const useDialCall = () =>
  usePoolMutation((input: DialCallInput) => telephonyService.dialCall(input));

// The list takes no params, so it sits on the `lists()` key rather than
// `list(params)`; mutations invalidate the module root.

export function useDidMappings() {
  return useQuery({
    queryKey: didMappingKeys.lists(),
    queryFn: () => telephonyService.listDidMappings(),
  });
}

export function useCreateDidMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: DidMappingInput) => telephonyService.createDidMapping(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: didMappingKeys.all() });
    },
  });
}

export function useUpdateDidMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateDidMappingInput }) =>
      telephonyService.updateDidMapping({ id, input }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: didMappingKeys.all() });
    },
  });
}

export function useDeleteDidMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => telephonyService.deleteDidMapping(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: didMappingKeys.all() });
    },
  });
}

/**
 * App-root listener (mounted in providers.tsx) so the ringing toast reaches
 * psychologists on any screen, not only the pool. Refetches the pool on every
 * pool change; toasts only when a call starts ringing. The server publishes
 * only to psychologists and chiefs at the call's location, so no role guard
 * is needed here.
 */
export function useTelephonyPoolListener(): void {
  const queryClient = useQueryClient();
  const handler = useCallback(
    (event: Extract<UserEvent, { type: 'telephony.pool-changed' }>) => {
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all() });
      if (event.action === 'ringing') toast.incomingCall(event.callerLabel);
    },
    [queryClient],
  );
  useRealtimeEvent('telephony.pool-changed', handler);
}

// The Ozonetel agent roster. Mutations invalidate the module root, and the
// dial options too, since the picker reads from this list.
export function useTelephonyAgents() {
  return useQuery({
    queryKey: telephonyAgentKeys.lists(),
    queryFn: () => telephonyService.listAgents(),
  });
}

function useAgentMutation<TVars, TResult>(mutationFn: (vars: TVars) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: telephonyAgentKeys.all() });
      void queryClient.invalidateQueries({ queryKey: telephonyKeys.all() });
    },
  });
}

export const useCreateTelephonyAgent = () =>
  useAgentMutation((input: TelephonyAgentInput) => telephonyService.createAgent(input));

export const useUpdateTelephonyAgent = () =>
  useAgentMutation((vars: { id: string; input: UpdateTelephonyAgentInput }) =>
    telephonyService.updateAgent(vars),
  );

export const useDeleteTelephonyAgent = () =>
  useAgentMutation((id: string) => telephonyService.deleteAgent(id));
